import React from 'react';
import {
    ActionButton,
    DefaultButton,
    Dropdown,
    Icon,
    IconButton,
    IDropdownOption,
    mergeStyles,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    Stack,
    Text,
    TextField,
    Toggle,
} from '@fluentui/react';
import { Css } from 'nano-css/addon/vcssom/cssToTree';
import { IHomePageConfig, INavPod, INavPodAttribute, INavPodFilter } from '../homePageConfig';
import { ISearchContainerStyle, ISearchElementStyle, ISearchTextStyle } from '../searchPageConfig';
import { advancedReplacementPatch, AdvancedStyleButton, elementStyleFrom, ElementPropertyRows, parseNum, StyleGroupAccordion } from '../styleControls';
import { ColorRow, ControlCell, StyleRow, StyleRowsWidthProvider, useCellControlStyles } from '../styleRows';
import { resolveHoverPart, STYLE_PART_ICONS } from '../stylesConfig';
import NavPodExample, { svgMaskStyle } from './NavPodExample';
import StorefrontSectionAdvancedStyleEditorModal from '../../StorefrontSectionAdvancedStyleEditorModal';
import '../StorefrontEditor.css';
import StorefrontBannerImageUploader from '../Banners/StorefrontBannerImageUploader';
import {
    useGetAllProductSegmentsQuery,
    useGetManufacturersQuery,
    useGetProductCategoriesBySegmentQuery,
    useGetSubCategoriesQuery,
} from '../../../../store/endpoints/ProductCategorizationEndpoints';
import { useGetStorefrontAvailableAttributesQuery } from '../../../../store/endpoints/StorefrontSettingsEndpoints';
import ProductTagsAutoSuggest from '../../../Search/ProductTagsAutoSuggest';
import SearchFilter from '../../../Common/SearchFilter';
import Setting, { DynamicSetting } from '../../../Common/Setting';
import ConditionalSetting from '../../../Common/ConditionalSetting';
import { useSettingReveal } from '../../../../hooks/useSettingReveal';

// react-autosuggest paints its own input chrome from App.css (30px tall, 1px #aaa border, 16px
// font), which is a second, differently-shaped box in a column of underlined cells. ControlCell owns
// the box on this surface, so the input is stripped to a transparent, borderless interior filling
// the cell, the same treatment useCellControlStyles gives the Fluent controls. The component sets
// `height: auto` inline on the input, which no class can beat, so the container centers it instead.
// The suggestions flyout is pulled up under the shorter cell (App.css positions it for the taller
// stock input) and lifted over the rows beneath it.
const tagCellClass = mergeStyles({
    width: '100%',
    height: '100%',
    selectors: {
        '& .react-autosuggest__container': { width: '100%', height: '100%', display: 'flex', alignItems: 'center' },
        '& .react-autosuggest__input': {
            width: '100%',
            border: 'none',
            borderRadius: 0,
            background: 'transparent',
            padding: '0 12px',
            fontFamily: 'inherit',
            fontSize: 13,
            fontWeight: 'normal',
        },
        '& .react-autosuggest__suggestions-container--open': { top: 34, zIndex: 100 },
    },
});

// Same tag UX as the product panel's Tags tab and the section editor: global-tag autosuggest with
// deletable chips underneath. Any number of tags; a pod matches products carrying ANY of them.
// The input takes a flat row like every other destination setting; the chips can't (they wrap to
// any height), so they sit on their own full-width line directly under it.
const TagPicker: React.FC<{ tags: string[]; onChange: (tags: string[]) => void }> = (props) => (
    <>
        {/* allowOverflow: the suggestion list is a plain child of the input's container, not a
            Fluent Layer, so a clipped cell would cut it off at 32px. */}
        <StyleRow label='Tags' allowOverflow>
            <div className={tagCellClass}>
                <ProductTagsAutoSuggest
                    editable
                    globalOnly
                    inputWidth='100%'
                    placeholder='Search Tags / Add Tag (Press Enter)'
                    onCreate={(tag) => {
                        if (props.tags.includes(tag)) return;
                        props.onChange([...props.tags, tag]);
                    }}
                />
            </div>
        </StyleRow>
        {props.tags.length > 0 && (
            <div>
                {props.tags.map((tag) => (
                    <SearchFilter key={tag} color='#9BD3AB' protected={false} onDelete={() => props.onChange(props.tags.filter((t) => t !== tag))}>
                        {tag}
                    </SearchFilter>
                ))}
            </div>
        )}
        <div style={{ fontSize: 12, color: '#605e5c', paddingBottom: 4 }}>
            Products carrying ANY of these tags match. Requires Product Tags enabled on the storefront.
        </div>
    </>
);

// Any number of attribute constraints. Same key OR's, different keys AND — the search page's own
// semantics, so a pod can say "Color: Black AND (RAM: 16GB OR RAM: 32GB)".
//
// Each constraint is one flat row: the attribute names the row, so its dropdown stands in for the
// label (quiet, borderless, so it reads as a label rather than a second control), and the value
// takes the shared control column. The delete button rides INSIDE that column so these rows still
// line up with the plain ones above them.
const AttributeRows: React.FC<{
    attributes: INavPodAttribute[];
    attributeOptions: IDropdownOption[];
    attributeValuesByKey: Record<string, string[]>;
    onChange: (attributes: INavPodAttribute[]) => void;
}> = (props) => {
    const cell = useCellControlStyles();
    const update = (i: number, patch: Partial<INavPodAttribute>) => {
        const next = [...props.attributes];
        next[i] = { ...next[i], ...patch };
        props.onChange(next);
    };
    return (
        <>
            {props.attributes.map((attr, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5em', minHeight: 36 }}>
                    <Dropdown
                        selectedKey={attr.key || null}
                        options={props.attributeOptions}
                        onChange={(_, o) => update(i, { key: (o?.key as string) || '', value: '' })}
                        placeholder='Attribute'
                        styles={{
                            root: { flex: '1 1 auto', minWidth: 0 },
                            title: { border: 'none', background: 'transparent', fontSize: 13, paddingLeft: 0, height: 30, lineHeight: '30px' },
                        }}
                    />
                    <ControlCell>
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                            <Dropdown
                                selectedKey={attr.value || null}
                                options={(props.attributeValuesByKey[attr.key] ?? []).map((v) => ({ key: v, text: v }))}
                                disabled={!attr.key}
                                onChange={(_, o) => update(i, { value: (o?.key as string) || '' })}
                                placeholder={!attr.key ? 'Pick an attribute first' : 'Any'}
                                styles={cell.dropdown}
                            />
                        </div>
                        <IconButton
                            iconProps={{ iconName: 'Delete' }}
                            ariaLabel='Remove attribute'
                            onClick={() => props.onChange(props.attributes.filter((_, x) => x !== i))}
                            styles={{ root: { height: '100%', width: 28, background: 'transparent' }, icon: { fontSize: 13 } }}
                        />
                    </ControlCell>
                </div>
            ))}
            {/* Centered icon-led action, same visual language as the style groups' "Advanced…" —
                boxed buttons on this surface kept reading as form controls rather than actions. */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '0.25em' }}>
                <ActionButton
                    iconProps={{ iconName: 'Add' }}
                    text='Add Attribute'
                    onClick={() => props.onChange([...props.attributes, { key: '', value: '' }])}
                    styles={{ root: { height: 28 }, label: { fontSize: 12 } }}
                />
            </div>
        </>
    );
};

// Blank = the storefront's default (centered when the pods wrap, left when they scroll).
const NAV_POD_ALIGN_OPTIONS: IDropdownOption[] = [
    { key: '', text: 'Default' },
    { key: 'left', text: 'Left' },
    { key: 'center', text: 'Center' },
    { key: 'right', text: 'Right' },
    { key: 'spaceBetween', text: 'Space between' },
];

// ISearchTextStyle / ISearchContainerStyle are narrower than the ISearchElementStyle the generic
// rows speak, so the dicts don't structurally interchange in either direction even though at
// RUNTIME both are open camelCase CSS bags with an index signature. One loose cast at the boundary,
// in both directions (value in, patch out), same treatment as ProductCardStylePanel's.
const loose = (dict?: { [prop: string]: any }): any => dict;

// The 560px panel affords a wider control column than the 180px the flat rows default to (that
// default is tuned for the 340px style panels).
const CONTROL_WIDTH = 260;

// width/height ride in the style bag's index signature, so they need a cast to read back out.
const sizeValue = (dict: ISearchElementStyle | undefined, key: 'width' | 'height'): string =>
    (dict?.[key] as number | undefined)?.toString() ?? '';

interface IStorefrontNavPodsEditorProps {
    isOpen: boolean;
    onDismiss: () => void;
    storefrontId: string;
    value?: INavPod[];
    onChange: (next: INavPod[]) => void;
    // Deep link from the home page canvas: expand this pod and scroll to it. One-shot: the parent
    // clears it via onInitialFocusHandled as soon as it's consumed, so clicking the same pod again
    // re-triggers the focus.
    initialFocusPodId?: string;
    onInitialFocusHandled?: () => void;
    // Same one-shot deal for the tab: the pod row's pencil lands on Pods, the band's on Style.
    initialPivot?: 'pods' | 'style';
    onInitialPivotHandled?: () => void;
    // The whole home page config: the Style tab edits its nav-pod keys, and the per-pod previews in
    // the Pods tab read the same dicts so both surfaces match the home page canvas.
    config: IHomePageConfig;
    onConfigChange: (patch: Partial<IHomePageConfig>) => void;
    // The storefront's background is dark, so the pod preview switches to its dark variant.
    isDark?: boolean;
}

// Exported so other home-page-config item editors (e.g. StorefrontAffiliationsEditor) mint ids
// the same way instead of duplicating this helper.
export const genId = (): string =>
    (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `navpod-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// A pod authored before multi-attribute support carries a single attributeKey/attributeValue pair.
// Fold it in as the first row so the merchant sees it; the editor drops the legacy pair the moment
// the list is touched, so the pod ends up in the new shape without needing a migration pass.
const podAttributes = (filter?: INavPodFilter): INavPodAttribute[] => [
    ...(filter?.attributes ?? []),
    ...(filter?.attributeKey && filter?.attributeValue && !(filter?.attributes ?? []).length
        ? [{ key: filter.attributeKey, value: filter.attributeValue }]
        : []),
];

// Complete constraints only — a half-filled row in the editor isn't a destination filter yet, and
// the storefront ignores it too.
const activeAttributes = (filter?: INavPodFilter): INavPodAttribute[] =>
    podAttributes(filter).filter((a) => !!a.key && !!a.value);

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// One-line description of where a pod sends the shopper, for the collapsed row and the Destination
// header. Mirrors the live precedence: a linkUrl wins outright; otherwise only the taxonomy levels
// that are actually set appear, so a category-only pod doesn't read as a broken path.
const destinationSummary = (pod: INavPod): string => {
    if (pod.linkUrl) return pod.linkUrl;
    const filter = pod.filter;
    const parts: string[] = [];
    const path = [filter?.segment, filter?.category, filter?.subCategory].filter((v): v is string => !!v);
    if (path.length) parts.push(path.join(' › '));
    if (filter?.manufacturer) parts.push(filter.manufacturer);
    const tags = (filter?.tags ?? []).filter((t) => !!t);
    if (tags.length) parts.push(plural(tags.length, 'tag'));
    const attrs = activeAttributes(filter);
    if (attrs.length) parts.push(plural(attrs.length, 'attribute'));
    return parts.length ? parts.join(' · ') : 'Everything (unfiltered search)';
};

// The small mode-toggle buttons (Image/SVG, Search/Link) share one look.
const pillButtonStyles = { root: { height: 26, padding: '0 8px', fontSize: 12, minWidth: 0 } };

// `as const` (rather than a React.CSSProperties annotation) so this spreads into both Fluent's
// IStyle and plain inline styles.
const ellipsisStyle = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;

// The pod's media as it reads at a glance in a collapsed row. Same precedence as the live store
// (SVG icon wins over image), so the thumbnail never advertises an image the shopper won't see.
const PodThumbnail: React.FC<{ pod: INavPod }> = ({ pod }) => (
    <div
        style={{
            width: 48, height: 48, flex: '0 0 auto', boxSizing: 'border-box',
            border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            color: '#605e5c',
        }}
    >
        {pod.iconSvg
            ? <span style={svgMaskStyle(pod.iconSvg, 28)} />
            : pod.imageUrl
                ? <img src={pod.imageUrl} alt='' style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                : <Icon iconName='Nav2DMapView' style={{ fontSize: 20, color: '#a19f9d' }} />}
    </div>
);

// Small semibold divider label inside the expanded form.
const FormSectionHeader: React.FC<{ label: string }> = (props) => (
    <div style={{ borderBottom: '1px solid #edebe9', paddingBottom: 2, marginTop: '0.5em' }}>
        <Text variant='small' styles={{ root: { fontWeight: 600 } }}>{props.label}</Text>
    </div>
);

// A single nav pod's row: a collapsed summary line that expands into the full editor. Kept as its
// own component so the category/sub-category dropdown queries (which depend on this pod's selected
// segment/category, and are skipped while collapsed) live at the row level rather than being shared
// across every pod.
const NavPodRow: React.FC<{
    pod: INavPod;
    storefrontId: string;
    expanded: boolean;
    onToggleExpanded: () => void;
    segmentOptions: IDropdownOption[];
    manufacturerOptions: IDropdownOption[];
    attributeOptions: IDropdownOption[];
    attributeValuesByKey: Record<string, string[]>;
    // Read for the live per-pod preview: the same nav-pod style keys the Style tab edits.
    config: IHomePageConfig;
    isDark?: boolean;
    rootRef?: (el: HTMLDivElement | null) => void;
    onChange: (pod: INavPod) => void;
    onRemove: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}> = (props) => {
    const { pod, expanded } = props;
    const reveal = useSettingReveal(expanded, props.onToggleExpanded);
    const filter = pod.filter ?? {};
    const cell = useCellControlStyles();

    // Collapsed rows fetch nothing — a long pod list would otherwise fan out two queries per pod.
    const categories = useGetProductCategoriesBySegmentQuery(filter.segment ?? '', { skip: !expanded || !filter.segment });
    const subCategories = useGetSubCategoriesQuery({ categories: filter.category ? [filter.category] : [] }, { skip: !expanded || !filter.category });

    // Which media input is shown. Seeded from the pod's own data; switching only changes what's
    // visible, never the stored value of the other field.
    const [mediaMode, setMediaMode] = React.useState<'image' | 'svg'>(pod.iconSvg ? 'svg' : 'image');
    // Which destination editor is shown — the search filter builder or a plain URL. Same
    // visibility-only semantics as mediaMode: toggling never clears the other side's data.
    const [destMode, setDestMode] = React.useState<'filter' | 'url'>(pod.linkUrl ? 'url' : 'filter');

    const categoryOptions: IDropdownOption[] = (categories.data?.items ?? [])
        .filter((c) => !!c.label)
        .map((c) => ({ key: c.label as string, text: c.label as string }));
    const subCategoryOptions: IDropdownOption[] = (subCategories.data?.items ?? []).map((s) => ({ key: s, text: s }));

    const setFilter = (patch: Partial<INavPodFilter>) => {
        props.onChange({ ...pod, filter: { ...filter, ...patch } });
    };
    const setAttributes = (next: INavPodAttribute[]) =>
        setFilter({ attributes: next, attributeKey: undefined, attributeValue: undefined });

    // This pod as the home page canvas renders it: the same component the Style tab pins at the top,
    // fed this pod's own content instead of the sample one. No caption and no hover simulation:
    // it sits inside the row being edited, not above a column of style settings.
    const podPreview = (
        <NavPodExample
            pod={pod}
            navPod={props.config.navPod}
            navPodName={props.config.navPodName}
            navPodImage={props.config.navPodImage}
            navPodLink={props.config.navPodLink}
            iconColor={props.config.navPodIconColor}
            isDark={props.isDark}
        />
    );

    return (
        <div
            {...reveal.revealProps}
            // Two refs on one node: the parent's, for scroll-into-view of a newly added pod, and the
            // reveal registration. Spread first so this ref wins.
            ref={(el) => {
                props.rootRef?.(el);
                (reveal.revealProps.ref as React.RefCallback<HTMLDivElement>)(el);
            }}
            style={{ border: '1px solid #edebe9', borderRadius: 4, background: '#fff' }}
        >
            {/* Collapsed summary line. The whole row toggles; the buttons stop propagation so they
                don't also fold/unfold the pod they act on. */}
            <Stack
                horizontal
                verticalAlign='center'
                tokens={{ childrenGap: '0.5em' }}
                styles={{ root: { padding: '0.5em', cursor: 'pointer' } }}
                onClick={props.onToggleExpanded}
            >
                <PodThumbnail pod={pod} />
                <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                    <Text block styles={{ root: { ...ellipsisStyle, color: pod.name ? undefined : '#a19f9d' } }}>
                        {pod.name || 'Untitled pod'}
                    </Text>
                    <Text variant='small' block styles={{ root: { ...ellipsisStyle, color: '#605e5c' } }}>
                        {destinationSummary(pod)}
                    </Text>
                </Stack.Item>
                <Stack.Item>
                    <Stack>
                        <IconButton
                            iconProps={{ iconName: 'ChevronUpSmall' }}
                            ariaLabel='Move up'
                            disabled={!props.onMoveUp}
                            onClick={(e) => { e.stopPropagation(); props.onMoveUp?.(); }}
                            styles={{ root: { height: 16 } }}
                        />
                        <IconButton
                            iconProps={{ iconName: 'ChevronDownSmall' }}
                            ariaLabel='Move down'
                            disabled={!props.onMoveDown}
                            onClick={(e) => { e.stopPropagation(); props.onMoveDown?.(); }}
                            styles={{ root: { height: 16 } }}
                        />
                    </Stack>
                </Stack.Item>
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    ariaLabel='Remove nav pod'
                    onClick={(e) => { e.stopPropagation(); props.onRemove(); }}
                />
                <IconButton
                    iconProps={{ iconName: expanded ? 'ChevronUp' : 'ChevronDown' }}
                    ariaLabel={expanded ? 'Collapse nav pod' : 'Expand nav pod'}
                    onClick={(e) => { e.stopPropagation(); props.onToggleExpanded(); }}
                />
            </Stack>

            {/* Pods are collapsed by default, so without this every link to a pod field would land on
                a page where the field genuinely isn't in the DOM. While a focus is pending the body
                renders hidden so the focus hook can find the field and ask this row to expand; the
                per-pod queries are still skipped while collapsed, so the hidden copy's dropdowns are
                empty — expanding for real is what fetches them. */}
            {(expanded || reveal.renderCollapsedChildren) && (
                <Stack
                    tokens={{ childrenGap: '0.5em' }}
                    // Spelled out rather than spreading reveal.hiddenStyle: that is a
                    // React.CSSProperties and Fluent's IStyle does not accept it.
                    styles={{ root: { padding: '0 0.75em 0.75em', display: expanded ? undefined : 'none' } }}
                >
                    {podPreview}

                    <FormSectionHeader label='Content' />
                    <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
                        <Stack.Item grow>
                            <DynamicSetting id={`storefront.navPodItem.${pod.id}.name`}>
                                <TextField
                                    label='Title'
                                    value={pod.name ?? ''}
                                    onChange={(_, v) => props.onChange({ ...pod, name: v || undefined })}
                                    placeholder='Nav pod title'
                                />
                            </DynamicSetting>
                        </Stack.Item>
                        <Stack.Item grow>
                            <DynamicSetting id={`storefront.navPodItem.${pod.id}.linkText`}>
                                <TextField
                                    label='Link Text'
                                    value={pod.linkText ?? ''}
                                    onChange={(_, v) => props.onChange({ ...pod, linkText: v || undefined })}
                                    placeholder='e.g. Shop now ›'
                                />
                            </DynamicSetting>
                        </Stack.Item>
                    </Stack>
                    {/* Compact artwork editor: a small pill toggle picks which input is visible
                        (the hidden field's value is kept — switching back loses nothing), and the
                        input itself stays small: a 64px uploader or a 2-row SVG paste box. */}
                    <Stack horizontal verticalAlign='start' tokens={{ childrenGap: '0.5em' }}>
                        <Stack tokens={{ childrenGap: '0.25em' }} styles={{ root: { flex: '0 0 auto' } }}>
                            <Text variant='small' styles={{ root: { fontWeight: 600 } }}>Artwork</Text>
                            <Stack horizontal tokens={{ childrenGap: '0.25em' }}>
                                <DefaultButton
                                    text='Image'
                                    iconProps={{ iconName: 'FileImage' }}
                                    primary={mediaMode === 'image'}
                                    onClick={() => setMediaMode('image')}
                                    styles={pillButtonStyles}
                                />
                                <DefaultButton
                                    text='SVG icon'
                                    iconProps={{ iconName: 'Shapes' }}
                                    primary={mediaMode === 'svg'}
                                    onClick={() => setMediaMode('svg')}
                                    styles={pillButtonStyles}
                                />
                            </Stack>
                        </Stack>
                        {mediaMode === 'image'
                            ? <div style={{ width: 64, height: 64, flex: '0 0 auto', border: '1px dashed #c8c6c4', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                <StorefrontBannerImageUploader
                                    value={pod.imageUrl}
                                    onChange={(url) => props.onChange({ ...pod, imageUrl: url })}
                                    width={64}
                                    height={64}
                                    imageId={pod.id}
                                    isNew={!pod.imageUrl}
                                />
                            </div>
                            : <Stack.Item grow>
                                <TextField
                                    multiline
                                    rows={2}
                                    value={pod.iconSvg ?? ''}
                                    onChange={(_, v) => props.onChange({ ...pod, iconSvg: v || undefined })}
                                    placeholder='<svg …>…</svg>'
                                    title='Recolorable icon — only its shape is used (tinted by the nav-pod icon color / hover color).'
                                />
                            </Stack.Item>}
                    </Stack>
                    {!!pod.imageUrl && !!pod.iconSvg && (
                        <Text variant='small' styles={{ root: { color: '#605e5c' } }}>
                            <Icon iconName='Info' style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            Both are set — the SVG icon takes precedence on the live store.
                        </Text>
                    )}

                    <FormSectionHeader label='Destination' />
                    {/* The two destination modes split the full row width (the procurement panels'
                        flexGrow pattern, e.g. ProcureDemandPanel's footer) — as small pills they
                        read as chips rather than the either/or choice they are. */}
                    <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
                        <DefaultButton
                            text='Search filter'
                            iconProps={{ iconName: 'Filter' }}
                            primary={destMode === 'filter'}
                            onClick={() => setDestMode('filter')}
                            style={{ flexGrow: 1 }}
                        />
                        <DefaultButton
                            text='Web link'
                            iconProps={{ iconName: 'Link' }}
                            primary={destMode === 'url'}
                            onClick={() => setDestMode('url')}
                            style={{ flexGrow: 1 }}
                        />
                    </Stack>
                    {/* Reflects the LIVE precedence (linkUrl wins), not the toggle — so if a set
                        link is shadowing the filter being edited, this line already says so. */}
                    <Text variant='small' styles={{ root: { color: '#605e5c' } }}>
                        Clicking the pod opens: <strong>{destinationSummary(pod)}</strong>
                    </Text>
                    {destMode === 'url'
                        ? <TextField
                            value={pod.linkUrl ?? ''}
                            onChange={(_, v) => props.onChange({ ...pod, linkUrl: v || undefined })}
                            placeholder='https://example.com — or an in-store path like /page/services'
                            description='External URLs open in a new tab; in-store paths navigate in place. Leave empty to use the search filter.'
                        />
                        : <>
                    {!!pod.linkUrl && (
                        <Text variant='small' styles={{ root: { color: '#605e5c' } }}>
                            <Icon iconName='Info' style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            A web link is set — it takes precedence over this filter on the live store.
                        </Text>
                    )}
                    {/* The destination settings read as a table: label left, control in the shared
                        fixed column, so the five filter levels line up instead of wrapping into a
                        ragged grid of labelled boxes (the same flat rows the Style tab uses). */}
                    <div>
                        <DynamicSetting id={`storefront.navPodItem.${pod.id}.segment`}>
                            <StyleRow label='Segment'>
                                <Dropdown
                                    selectedKey={filter.segment ?? null}
                                    options={props.segmentOptions}
                                    onChange={(_, o) => setFilter({ segment: (o?.key as string) || undefined, category: undefined, subCategory: undefined })}
                                    placeholder='Any'
                                    styles={cell.dropdown}
                                />
                            </StyleRow>
                        </DynamicSetting>
                        {/* Progressive disclosure: each level only appears once the one above it
                            is set, so an empty filter is two controls rather than five. */}
                        <ConditionalSetting when={!!filter.segment} idPrefix={`storefront.navPodItem.${pod.id}.category`}>
                            <DynamicSetting id={`storefront.navPodItem.${pod.id}.category`}>
                                <StyleRow label='Category'>
                                    <Dropdown
                                        selectedKey={filter.category ?? null}
                                        options={categoryOptions}
                                        disabled={categories.isFetching || categoryOptions.length === 0}
                                        onChange={(_, o) => setFilter({ category: (o?.key as string) || undefined, subCategory: undefined })}
                                        placeholder={categories.isFetching ? 'Loading…' : 'Any'}
                                        styles={cell.dropdown}
                                    />
                                </StyleRow>
                            </DynamicSetting>
                        </ConditionalSetting>
                        <ConditionalSetting when={!!filter.category} idPrefix={`storefront.navPodItem.${pod.id}.subCategory`}>
                            <DynamicSetting id={`storefront.navPodItem.${pod.id}.subCategory`}>
                                <StyleRow label='Sub Category'>
                                    <Dropdown
                                        selectedKey={filter.subCategory ?? null}
                                        options={subCategoryOptions}
                                        disabled={subCategories.isFetching || subCategoryOptions.length === 0}
                                        onChange={(_, o) => setFilter({ subCategory: (o?.key as string) || undefined })}
                                        placeholder={subCategories.isFetching ? 'Loading…' : 'Any'}
                                        styles={cell.dropdown}
                                    />
                                </StyleRow>
                            </DynamicSetting>
                        </ConditionalSetting>
                        <DynamicSetting id={`storefront.navPodItem.${pod.id}.manufacturer`}>
                            <StyleRow label='Manufacturer'>
                                <Dropdown
                                    selectedKey={filter.manufacturer ?? null}
                                    options={props.manufacturerOptions}
                                    onChange={(_, o) => setFilter({ manufacturer: (o?.key as string) || undefined })}
                                    placeholder='Any'
                                    styles={cell.dropdown}
                                />
                            </StyleRow>
                        </DynamicSetting>
                        <TagPicker
                            tags={filter.tags ?? []}
                            onChange={(tags) => setFilter({ tags: tags.length ? tags : undefined })}
                        />
                        <AttributeRows
                            attributes={podAttributes(filter)}
                            attributeOptions={props.attributeOptions}
                            attributeValuesByKey={props.attributeValuesByKey}
                            onChange={setAttributes}
                        />
                    </div>
                        </>}
                </Stack>
            )}
        </div>
    );
};

// Every nav-pod style key in one surface, with the live example pinned above it. The groups are the
// Storefront Styles panel's part accordions, one open at a time, each ending in the same
// "Advanced…" escape into the raw key/value CSS editor. (No named-style picker: none of these keys
// maps to a style element type, so there is nothing to offer.) The dimension/alignment/scroll
// settings sit in the Pod group because that is where a merchant looks for them, even though they
// aren't CSS on the tile.
const NavPodStyleTab: React.FC<{
    config: IHomePageConfig;
    onChange: (patch: Partial<IHomePageConfig>) => void;
    isDark?: boolean;
}> = ({ config, onChange, isDark }) => {
    const cell = useCellControlStyles();
    const updatePod = (patch: Partial<ISearchElementStyle>) => onChange({ navPod: { ...config.navPod, ...patch } });
    const updateName = (patch: Partial<ISearchTextStyle>) => onChange({ navPodName: { ...config.navPodName, ...patch } });
    const updateLink = (patch: Partial<ISearchTextStyle>) => onChange({ navPodLink: { ...config.navPodLink, ...patch } });
    const updateImage = (patch: Partial<ISearchElementStyle>) => onChange({ navPodImage: { ...config.navPodImage, ...patch } });
    const updateContainer = (patch: Partial<ISearchContainerStyle>) => onChange({ navPodContainer: { ...config.navPodContainer, ...patch } });
    const updateHover = (patch: Partial<ISearchElementStyle>) => onChange({ navPodHover: { ...config.navPodHover, ...patch } });

    // Exactly the renderer's pipeline (HomePage.tsx): resolveHoverPart picks ONE ref (the hover
    // dict's own, else the resting pod's) and puts that preset's `hover` part UNDER the local
    // navPodHover dict. The example consumes it; the live store emits it as a :hover rule.
    const hoverStyle: React.CSSProperties =
        elementStyleFrom(loose(resolveHoverPart(config.navPodHover, config.navPod)), 'hover');

    // One group open at a time, seeded on the pod itself (the part a merchant reaches for first) —
    // the same accordion behaviour as the Storefront Styles panel's part list, so six full property
    // sets don't stack into one endless column.
    const [openGroup, setOpenGroup] = React.useState('pod');
    const group = (key: string) => ({
        expanded: openGroup === key,
        onToggle: (expanded: boolean) => setOpenGroup(expanded ? key : ''),
    });

    // The raw key/value CSS editor, one entry per group. It hands back the WHOLE dict rather than a
    // patch (a key deleted there has to disappear), so each entry goes through
    // advancedReplacementPatch instead of merging.
    const [advancedGroup, setAdvancedGroup] = React.useState<string | undefined>();
    const advanced: Record<string, { value?: Record<string, any>; replace: (css: Css) => void }> = {
        pod: { value: config.navPod, replace: (css) => updatePod(advancedReplacementPatch(config.navPod, css)) },
        title: { value: config.navPodName, replace: (css) => updateName(advancedReplacementPatch(config.navPodName, css)) },
        link: { value: config.navPodLink, replace: (css) => updateLink(advancedReplacementPatch(config.navPodLink, css)) },
        image: { value: config.navPodImage, replace: (css) => updateImage(advancedReplacementPatch(config.navPodImage, css)) },
        container: { value: config.navPodContainer, replace: (css) => updateContainer(advancedReplacementPatch(config.navPodContainer, css)) },
        hover: { value: config.navPodHover, replace: (css) => updateHover(advancedReplacementPatch(config.navPodHover, css)) },
    };

    // The rows' control-column width comes from the panel's StyleRowsWidthProvider, which covers
    // both tabs.
    return (
        <>
            {/* Pinned above the groups so every row's effect is on screen without scrolling back to
                the preview canvas (sf-card-example-sticky, StorefrontEditor.css). */}
            <div className='sf-card-example-sticky' style={{ marginTop: 8 }}>
                <NavPodExample
                    navPod={config.navPod}
                    navPodName={config.navPodName}
                    navPodImage={config.navPodImage}
                    navPodLink={config.navPodLink}
                    iconColor={config.navPodIconColor}
                    iconHoverColor={config.navPodIconHoverColor}
                    hoverStyle={hoverStyle}
                    isDark={isDark}
                    caption='Example pod, updates as you edit'
                />
            </div>

            <StyleGroupAccordion
                label='Pod'
                iconName={STYLE_PART_ICONS.card}
                tooltip='The pod tile itself. Width and height apply to every pod so the row stays uniform; blank leaves the storefront default of 140 by 180.'
                {...group('pod')}
            >
                <Setting id='storefront.navPods.width'>
                    <StyleRow label='Width'>
                        <TextField type='number' min={0} suffix='px' value={sizeValue(config.navPod, 'width')} onChange={(_, v) => updatePod({ width: parseNum(v) })} styles={cell.textField} />
                    </StyleRow>
                </Setting>
                <Setting id='storefront.navPods.height'>
                    <StyleRow label='Height'>
                        <TextField type='number' min={0} suffix='px' value={sizeValue(config.navPod, 'height')} onChange={(_, v) => updatePod({ height: parseNum(v) })} styles={cell.textField} />
                    </StyleRow>
                </Setting>
                <Setting id='storefront.navPods.align'>
                    <StyleRow label='Alignment'>
                        <Dropdown
                            options={NAV_POD_ALIGN_OPTIONS}
                            selectedKey={config.navPodAlign ?? ''}
                            onChange={(_, o) => onChange({ navPodAlign: (o?.key as IHomePageConfig['navPodAlign']) || undefined })}
                            styles={cell.dropdown}
                        />
                    </StyleRow>
                </Setting>
                <Setting id='storefront.navPods.overflow'>
                    <StyleRow label='Overflow'>
                        <Toggle
                            checked={!!config.navPodScroll}
                            onText='Scroll (arrows)'
                            offText='Wrap to next line'
                            onChange={(_, checked) => onChange({ navPodScroll: checked || undefined })}
                            styles={{ root: { margin: 0, height: '100%', display: 'flex', alignItems: 'center', paddingLeft: 12 }, text: { fontSize: 13 } }}
                        />
                    </StyleRow>
                </Setting>
                <ElementPropertyRows value={config.navPod} settingKeyPrefix='storefront.navPod.pod' onChange={updatePod} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('pod')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Title'
                iconName={STYLE_PART_ICONS.title}
                tooltip='The pod title. Font settings here style that line only.'
                {...group('title')}
            >
                <ElementPropertyRows value={loose(config.navPodName)} settingKeyPrefix='storefront.navPod.title' onChange={(patch) => updateName(loose(patch))} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('title')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Link'
                iconName='Link'
                tooltip='The call-to-action line under the title (each pod supplies its own text).'
                {...group('link')}
            >
                <ElementPropertyRows value={loose(config.navPodLink)} settingKeyPrefix='storefront.navPod.link' onChange={(patch) => updateLink(loose(patch))} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('link')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Image'
                iconName={STYLE_PART_ICONS.image}
                tooltip='The pod artwork. Blank width and height leave the default 64px square. Icon color tints recolorable SVG icons only; an uploaded image keeps its own colors.'
                {...group('image')}
            >
                <Setting id='storefront.navPodImage.width'>
                    <StyleRow label='Width'>
                        <TextField type='number' min={0} suffix='px' value={sizeValue(config.navPodImage, 'width')} onChange={(_, v) => updateImage({ width: parseNum(v) })} styles={cell.textField} />
                    </StyleRow>
                </Setting>
                <Setting id='storefront.navPodImage.height'>
                    <StyleRow label='Height'>
                        <TextField type='number' min={0} suffix='px' value={sizeValue(config.navPodImage, 'height')} onChange={(_, v) => updateImage({ height: parseNum(v) })} styles={cell.textField} />
                    </StyleRow>
                </Setting>
                {/* Only recolorable SVG icons take a tint; an uploaded image keeps its own colors. */}
                <ColorRow label='Icon color' value={config.navPodIconColor} onChange={(v) => onChange({ navPodIconColor: v })} />
                <ElementPropertyRows value={config.navPodImage} settingKeyPrefix='storefront.navPod.image' onChange={updateImage} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('image')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Container'
                iconName={STYLE_PART_ICONS.band}
                tooltip='The row that wraps the pods, not the pods themselves.'
                {...group('container')}
            >
                <ElementPropertyRows value={loose(config.navPodContainer)} settingKeyPrefix='storefront.navPod.container' onChange={(patch) => updateContainer(loose(patch))} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('container')} />
            </StyleGroupAccordion>

            <StyleGroupAccordion
                label='Hover'
                iconName={STYLE_PART_ICONS.hover}
                tooltip="Applied while the shopper's pointer is over the pod - the storefront emits it as a scoped :hover rule with a short transition. Icon hover color recolors a recolorable SVG icon for the same moment."
                {...group('hover')}
            >
                <ColorRow label='Icon hover color' value={config.navPodIconHoverColor} onChange={(v) => onChange({ navPodIconHoverColor: v })} />
                <ElementPropertyRows value={config.navPodHover} settingKeyPrefix='storefront.navPod.hover' onChange={updateHover} />
                <AdvancedStyleButton onClick={() => setAdvancedGroup('hover')} />
            </StyleGroupAccordion>

            {/* Rendered once, outside the accordions: the raw CSS editor for whichever group asked. */}
            {advancedGroup && advanced[advancedGroup] && (
                <StorefrontSectionAdvancedStyleEditorModal
                    css={(advanced[advancedGroup].value ?? {}) as Css}
                    displayModal
                    closer={() => setAdvancedGroup(undefined)}
                    onChange={(css) => advanced[advancedGroup].replace(css)}
                />
            )}
        </>
    );
};

const StorefrontNavPodsEditor: React.FC<IStorefrontNavPodsEditorProps> = (props) => {
    const pods = props.value ?? [];

    // Accordion: exactly one pod open at a time, so the panel stays a scannable list.
    const [expandedPodId, setExpandedPodId] = React.useState<string | undefined>(undefined);
    // Which tab is showing. Local (not derived from the prop) so the merchant's own tab clicks
    // stick; the deep link below only seeds it.
    const [pivot, setPivot] = React.useState<'pods' | 'style'>('pods');
    // Set when a pod should be scrolled to once its row has mounted (adding a pod).
    const [scrollToPodId, setScrollToPodId] = React.useState<string | undefined>(undefined);
    const rowRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

    const segments = useGetAllProductSegmentsQuery();
    const manufacturers = useGetManufacturersQuery();
    const attrs = useGetStorefrontAvailableAttributesQuery(props.storefrontId, { skip: !props.storefrontId });

    const segmentOptions: IDropdownOption[] = (segments.data?.items ?? [])
        .filter((s) => !!s.label)
        .map((s) => ({ key: s.label as string, text: s.label as string }));
    const manufacturerOptions: IDropdownOption[] = (manufacturers.data?.items ?? []).map((m) => ({ key: m, text: m }));
    const attributeOptions: IDropdownOption[] = (attrs.data?.attributes ?? []).map((a) => ({ key: a.key, text: a.key }));
    const attributeValuesByKey = React.useMemo(() => {
        const map: Record<string, string[]> = {};
        (attrs.data?.attributes ?? []).forEach((a) => { map[a.key] = a.values ?? []; });
        return map;
    }, [attrs.data?.attributes]);

    // Deep link from the canvas. Consumed immediately (onInitialFocusHandled) so the parent's
    // one-shot state resets and clicking the same pod again re-fires this. The scroll itself is
    // latched into local scrollToPodId — consuming the prop re-renders with it undefined, and a
    // timer owned by THIS effect would be cleaned up on that re-render before ever firing.
    React.useEffect(() => {
        if (!props.isOpen || !props.initialFocusPodId) return;
        setExpandedPodId(props.initialFocusPodId);
        setScrollToPodId(props.initialFocusPodId);
        props.onInitialFocusHandled?.();
    }, [props.isOpen, props.initialFocusPodId]);

    // Same one-shot pattern for the tab: the canvas says which one it meant, the merchant's own
    // clicks own it from then on.
    React.useEffect(() => {
        if (!props.isOpen || !props.initialPivot) return;
        setPivot(props.initialPivot);
        props.onInitialPivotHandled?.();
    }, [props.isOpen, props.initialPivot]);

    // A freshly added pod only gets its ref on the render after onChange lands, so this waits for
    // the row to exist rather than clearing the request on the first pass. The 250ms delay waits
    // out the Panel's Layer slide-in (deep-link case) — scrolling mid-animation is a no-op.
    React.useEffect(() => {
        if (!scrollToPodId) return;
        const row = rowRefs.current[scrollToPodId];
        if (!row) return;
        const timer = window.setTimeout(() => {
            row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            setScrollToPodId(undefined);
        }, 250);
        return () => window.clearTimeout(timer);
    }, [scrollToPodId, pods.length]);

    const updatePod = (index: number, pod: INavPod) => {
        const next = [...pods];
        next[index] = pod;
        props.onChange(next);
    };
    const removePod = (index: number) => {
        const removed = pods[index];
        if (removed) {
            if (expandedPodId === removed.id) setExpandedPodId(undefined);
            delete rowRefs.current[removed.id];
        }
        props.onChange(pods.filter((_, i) => i !== index));
    };
    const movePod = (index: number, dir: -1 | 1) => {
        const target = index + dir;
        if (target < 0 || target >= pods.length) return;
        const next = [...pods];
        [next[index], next[target]] = [next[target], next[index]];
        props.onChange(next);
    };
    const addPod = () => {
        const pod: INavPod = { id: genId() };
        props.onChange([...pods, pod]);
        setExpandedPodId(pod.id);
        setScrollToPodId(pod.id);
    };

    return (
        <Panel
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            headerText='Nav Pods'
            type={PanelType.custom}
            customWidth='560px'
            isBlocking={false}
            closeButtonAriaLabel='Close'
        >
            {/* The pods and their styling are one subject, so they're one panel: the canvas pencils
                land on whichever tab the merchant asked for (see initialPivot). The flat rows on
                BOTH tabs share the wider control column this panel affords. */}
            <StyleRowsWidthProvider width={CONTROL_WIDTH}>
            <Pivot
                style={{ marginTop: 8 }}
                selectedKey={pivot}
                onLinkClick={(item) => setPivot((item?.props.itemKey as 'pods' | 'style') ?? 'pods')}
            >
                <PivotItem itemKey='pods' headerText='Pods'>
                    <Stack tokens={{ childrenGap: '0.5em' }} style={{ marginTop: 8 }}>
                        <Text variant='small' styles={{ root: { color: '#605e5c' } }}>
                            Nav pods are large, image-backed shortcuts shown above the category pods. Each navigates
                            shoppers to a filtered search.
                        </Text>
                        {pods.length === 0 && (
                            <Text styles={{ root: { color: '#605e5c' } }}>No nav pods yet. Add one to get started.</Text>
                        )}
                        {pods.map((pod, index) => (
                            <NavPodRow
                                key={pod.id}
                                pod={pod}
                                storefrontId={props.storefrontId}
                                expanded={expandedPodId === pod.id}
                                onToggleExpanded={() => setExpandedPodId(expandedPodId === pod.id ? undefined : pod.id)}
                                segmentOptions={segmentOptions}
                                manufacturerOptions={manufacturerOptions}
                                attributeOptions={attributeOptions}
                                attributeValuesByKey={attributeValuesByKey}
                                config={props.config}
                                isDark={props.isDark}
                                rootRef={(el) => { rowRefs.current[pod.id] = el; }}
                                onChange={(p) => updatePod(index, p)}
                                onRemove={() => removePod(index)}
                                onMoveUp={index > 0 ? () => movePod(index, -1) : undefined}
                                onMoveDown={index < pods.length - 1 ? () => movePod(index, 1) : undefined}
                            />
                        ))}
                        {/* Centered icon-led action, matching Add Attribute above and the style
                            groups' "Advanced…" — the boxed primary button here outweighed the list
                            it appends to. */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <ActionButton
                                iconProps={{ iconName: 'Add' }}
                                text='Add Nav Pod'
                                onClick={addPod}
                                styles={{ root: { height: 28 }, label: { fontSize: 12 } }}
                            />
                        </div>
                        <DefaultButton text='Done' onClick={props.onDismiss} />
                    </Stack>
                </PivotItem>
                <PivotItem itemKey='style' headerText='Style'>
                    <NavPodStyleTab config={props.config} onChange={props.onConfigChange} isDark={props.isDark} />
                </PivotItem>
            </Pivot>
            </StyleRowsWidthProvider>
        </Panel>
    );
};

export default StorefrontNavPodsEditor;
